/**
 * Mobile sync bridge.
 *
 * Runs only on the mobile (Capacitor) platform.
 * After login: registers the device, pulls server state, and sets up
 * push notifications.
 */
import { platform, isMobile } from "@platform";
import { getAccessToken } from "./local-storage-handler";
import syncApi from "@interceptors/sync-instance";
import { pullAllOnLogin } from "./sync-handler";

const API = "/api/v1";

/**
 * Register this mobile device and its FCM push token with the server.
 */
async function registerMobileDevice(): Promise<void> {
  if (!isMobile || !getAccessToken()) return;

  try {
    const deviceId = await platform.getDeviceId();
    const deviceName = platform.getDeviceName();

    await syncApi.post(`${API}/auth/devices`, {
      device_id: deviceId,
      device_name: deviceName,
      platform: "mobile",
    });

    // Register push token
    const pushToken = await platform.getPushToken();
    if (pushToken) {
      await syncApi.put(`${API}/auth/devices/push-token`, {
        device_id: deviceId,
        push_token: pushToken,
      });
    }
  } catch (e) {
    console.warn("[MobileSync] Device registration failed:", e);
  }
}

/**
 * Full mobile sync on login.
 *
 * 1. Register device.
 * 2. Pull all user data from server.
 *
 * Note: push notification permission is intentionally NOT requested here.
 * FCM/Firebase is not configured for this app, so requesting permission
 * and then calling PushNotifications.register() would crash the app.
 */
export async function mobilePostLoginSync(): Promise<void> {
  if (!isMobile) return;

  try {
    await registerMobileDevice();
    await pullAllOnLogin();
  } catch (e) {
    console.warn("[MobileSync] Post-login sync partially failed:", e);
  }
}
